import { pick, values, forEach, includes } from "lodash";

import type { Amenity } from "@busbud/int-schemas";

import type { TranslatorService } from "@app/services/translator";
import type { StringKeyOf } from "@app/types/meta-types";
import type {
  NapiAmenities,
  NapiClassAmenities,
  NapiOperator
} from "@app/types/napi-types";
import type { TripOption } from "@app/types/results-types";

// Amenities as returned by napi
export const BACKEND_AMENITIES = {
  AC: "ac",
  AVERAGE_SEAT: "average_seat",
  BUS_ATTENDANT: "bus_attendant", // Not used anymore
  CAFE: "cafe",
  CHECKED_BAGGAGE: "checked_baggage",
  FOOD: "food",
  FULL_RECLINE_SEAT: "full_recline_seat",
  HOT_MEAL: "hot_meal",
  LEG_ROOM: "leg_room", // Not used anymore
  LOUNGE_ACCESS: "lounge_access",
  POWER_OUTLETS: "power_outlets",
  QUIET_ZONE: "quiet_zone",
  REFRESHMENT: "refreshment",
  RESERVED_SEATS: "reserved_seats",
  RESTAURANT: "restaurant",
  SMALL_SEAT: "small_seat",
  TOILET: "toilet",
  TRAY_MEAL: "tray_meal",
  TV: "tv",
  TWO_PLUS_ONE: "2+1",
  TWO_PLUS_TWO: "2+2",
  WHEELCHAIR_RAMP: "wheelchair_ramp",
  WIFI: "wifi",
  XL_SEAT: "xl_seat"
};

// Amenities as actually used in the frontend
export const DISPLAYABLE_AMENITIES = {
  AC: "ac",
  AVERAGE_SEAT: "average_seat",
  CAFE: "cafe",
  DRINK: "drink",
  ENTERTAINMENT: "entertainment",
  ENTERTAINMENT_AND_WIFI: "entertainment_and_wifi",
  FULL_RECLINE_SEAT: "full_recline_seat",
  HOT_MEAL: "hot_meal",
  CHECKED_BAGGAGE: "checked_baggage",
  LOUNGE_ACCESS: "lounge_access",
  NO_AC: "no_ac",
  NO_FOOD: "no_food",
  NO_POWER_OUTLETS: "no_power_outlets",
  NO_E_TICKET: "no_e_ticket",
  NO_TOILET: "no_toilet",
  NO_WIFI: "no_wifi",
  POWER_OUTLETS: "power_outlets",
  QUIET_ZONE: "quiet_zone",
  E_TICKET: "e_ticket",
  RESTAURANT: "restaurant",
  RESERVED_SEATS: "reserved_seats",
  SMALL_SEAT: "small_seat",
  SNACK: "snack",
  TOILET: "toilet",
  TRAY_MEAL: "tray_meal",
  TWO_PLUS_ONE: "2+1",
  TWO_PLUS_TWO: "2+2",
  WHEELCHAIR_RAMP: "wheelchair_ramp",
  WIFI: "wifi",
  XL_SEAT: "xl_seat"
};

const SEAT_TYPE_KEYS = [
  DISPLAYABLE_AMENITIES.SMALL_SEAT,
  DISPLAYABLE_AMENITIES.AVERAGE_SEAT,
  DISPLAYABLE_AMENITIES.XL_SEAT,
  DISPLAYABLE_AMENITIES.FULL_RECLINE_SEAT
];

export function isAmenitiesArray(
  amenities: NapiAmenities | Amenity[] | string[]
): amenities is Amenity[] | string[] {
  return Array.isArray(amenities);
}

export function filterSupportedAmenities(
  amenities: Partial<NapiAmenities> | NapiClassAmenities,
  filter_override: typeof BACKEND_AMENITIES = BACKEND_AMENITIES
): (keyof NapiAmenities)[] {
  const supported_amenities = pick(amenities, values(filter_override));
  return Object.keys(supported_amenities).filter(
    key => supported_amenities[key as keyof typeof supported_amenities] === true
  );
}

/**
 * Takes an array of amenity names (the output of `{@link filterSupportedAmenities}`) and returns an array of amenity names
 * matching those we want to use to actually display them. The names returned by this function may be used to obtain
 * the proper icons (using `getIcon`) and translations.
 *
 * @param filtered_amenities
 */
export function getDisplayableAmenityKeys(
  filtered_amenities: (keyof NapiAmenities)[]
): StringKeyOf<NapiAmenities>[] {
  const displayable_amenities: string[] = [];

  const has_air_conditioning = includes(
    filtered_amenities,
    BACKEND_AMENITIES.AC
  );

  const has_toilet = includes(filtered_amenities, BACKEND_AMENITIES.TOILET);

  const has_tv = includes(filtered_amenities, BACKEND_AMENITIES.TV);
  const has_wifi = includes(filtered_amenities, BACKEND_AMENITIES.WIFI);

  const has_power_outlets = includes(
    filtered_amenities,
    BACKEND_AMENITIES.POWER_OUTLETS
  );

  const has_food = includes(filtered_amenities, BACKEND_AMENITIES.FOOD); // Snack
  const has_refreshment = includes(
    filtered_amenities,
    BACKEND_AMENITIES.REFRESHMENT
  ); // Drink
  const has_hot_meal = includes(filtered_amenities, BACKEND_AMENITIES.HOT_MEAL);
  const has_cafe = includes(filtered_amenities, BACKEND_AMENITIES.CAFE);
  const has_checked_baggage = includes(
    filtered_amenities,
    BACKEND_AMENITIES.CHECKED_BAGGAGE
  );
  const has_reserved_seat = includes(
    filtered_amenities,
    BACKEND_AMENITIES.RESERVED_SEATS
  );
  const has_quiet_zone = includes(
    filtered_amenities,
    BACKEND_AMENITIES.QUIET_ZONE
  );
  const has_wheelchair_ramp = includes(
    filtered_amenities,
    BACKEND_AMENITIES.WHEELCHAIR_RAMP
  );
  const has_tray_meal = includes(
    filtered_amenities,
    BACKEND_AMENITIES.TRAY_MEAL
  );

  const has_restaurant = includes(
    filtered_amenities,
    BACKEND_AMENITIES.RESTAURANT
  );

  if (has_air_conditioning) {
    displayable_amenities.push(DISPLAYABLE_AMENITIES.AC);
  }
  if (has_toilet) {
    displayable_amenities.push(DISPLAYABLE_AMENITIES.TOILET);
  }

  if (includes(filtered_amenities, BACKEND_AMENITIES.TWO_PLUS_ONE)) {
    displayable_amenities.push(DISPLAYABLE_AMENITIES.TWO_PLUS_ONE);
  }

  if (includes(filtered_amenities, BACKEND_AMENITIES.TWO_PLUS_TWO)) {
    displayable_amenities.push(DISPLAYABLE_AMENITIES.TWO_PLUS_TWO);
  }

  forEach(SEAT_TYPE_KEYS, function (seat_type_key) {
    if (includes(filtered_amenities, seat_type_key)) {
      displayable_amenities.push(seat_type_key);
    }
  });

  if (has_wifi) {
    displayable_amenities.push(DISPLAYABLE_AMENITIES.WIFI);
  }
  if (has_tv) {
    displayable_amenities.push(DISPLAYABLE_AMENITIES.ENTERTAINMENT);
  }

  if (has_power_outlets) {
    displayable_amenities.push(DISPLAYABLE_AMENITIES.POWER_OUTLETS);
  }

  if (has_food) {
    displayable_amenities.push(DISPLAYABLE_AMENITIES.SNACK);
  }
  if (has_refreshment) {
    displayable_amenities.push(DISPLAYABLE_AMENITIES.DRINK);
  }
  if (has_hot_meal) {
    displayable_amenities.push(DISPLAYABLE_AMENITIES.HOT_MEAL);
  }
  if (has_reserved_seat) {
    displayable_amenities.push(DISPLAYABLE_AMENITIES.RESERVED_SEATS);
  }
  if (has_quiet_zone) {
    displayable_amenities.push(DISPLAYABLE_AMENITIES.QUIET_ZONE);
  }
  if (has_checked_baggage) {
    displayable_amenities.push(DISPLAYABLE_AMENITIES.CHECKED_BAGGAGE);
  }
  if (has_cafe) {
    displayable_amenities.push(DISPLAYABLE_AMENITIES.CAFE);
  }
  if (has_wheelchair_ramp) {
    displayable_amenities.push(DISPLAYABLE_AMENITIES.WHEELCHAIR_RAMP);
  }
  if (has_tray_meal) {
    displayable_amenities.push(DISPLAYABLE_AMENITIES.TRAY_MEAL);
  }
  if (has_restaurant) {
    displayable_amenities.push(DISPLAYABLE_AMENITIES.RESTAURANT);
  }

  return displayable_amenities;
}

export function getLabelText(
  translator: TranslatorService,
  amenity_key: keyof NapiAmenities
) {
  switch (amenity_key) {
    case DISPLAYABLE_AMENITIES.AC:
      return translator.t("!results.amenities.ac");
    case DISPLAYABLE_AMENITIES.AVERAGE_SEAT:
      return translator.t("!results.amenities.average-seat");
    case DISPLAYABLE_AMENITIES.CAFE:
      return translator.t("!results.amenities.cafe");
    case DISPLAYABLE_AMENITIES.DRINK:
      return translator.t("!results.amenities.drink");
    case DISPLAYABLE_AMENITIES.ENTERTAINMENT:
      return translator.t("!results.amenities.entertainment");
    case DISPLAYABLE_AMENITIES.ENTERTAINMENT_AND_WIFI:
      return translator.t("!results.amenities.entertainment-and-wifi");
    case DISPLAYABLE_AMENITIES.FULL_RECLINE_SEAT:
      return translator.t("!results.amenities.full-recline-seat");
    case DISPLAYABLE_AMENITIES.HOT_MEAL:
      return translator.t("!results.amenities.hot-meal");
    case DISPLAYABLE_AMENITIES.CHECKED_BAGGAGE:
      return translator.t("!results.amenities.checked-baggage");
    case DISPLAYABLE_AMENITIES.LOUNGE_ACCESS:
      return translator.t("!results.amenities.lounge-access");
    case DISPLAYABLE_AMENITIES.NO_AC:
      return translator.t("!results.amenities.no-ac");
    case DISPLAYABLE_AMENITIES.NO_FOOD:
      return translator.t("!results.amenities.no-food");
    case DISPLAYABLE_AMENITIES.NO_POWER_OUTLETS:
      return translator.t("!results.amenities.no-power-outlets");
    case DISPLAYABLE_AMENITIES.NO_TOILET:
      return translator.t("!results.amenities.no-toilet");
    case DISPLAYABLE_AMENITIES.NO_WIFI:
      return translator.t("!results.amenities.no-wifi");
    case DISPLAYABLE_AMENITIES.POWER_OUTLETS:
      return translator.t("!results.amenities.power-outlets");
    case DISPLAYABLE_AMENITIES.QUIET_ZONE:
      return translator.t("!results.amenities.quiet-zone");
    case DISPLAYABLE_AMENITIES.RESERVED_SEATS:
      return translator.t("!results.amenities.reserved-seats");
    case DISPLAYABLE_AMENITIES.RESTAURANT:
      return translator.t("!results.amenities.restaurant");
    case DISPLAYABLE_AMENITIES.SMALL_SEAT:
      return translator.t("!results.amenities.small-seat");
    case DISPLAYABLE_AMENITIES.SNACK:
      return translator.t("!results.amenities.snack");
    case DISPLAYABLE_AMENITIES.TOILET:
      return translator.t("!results.amenities.toilet");
    case DISPLAYABLE_AMENITIES.TRAY_MEAL:
      return translator.t("!results.amenities.tray-meal");
    case DISPLAYABLE_AMENITIES.TWO_PLUS_ONE:
      return translator.t("!results.amenities.two_plus_one");
    case DISPLAYABLE_AMENITIES.TWO_PLUS_TWO:
      return translator.t("!results.amenities.two_plus_two");
    case DISPLAYABLE_AMENITIES.WHEELCHAIR_RAMP:
      return translator.t("!results.amenities.wheelchair-ramp");
    case DISPLAYABLE_AMENITIES.WIFI:
      return translator.t("!results.amenities.wifi");
    case DISPLAYABLE_AMENITIES.XL_SEAT:
      return translator.t("!results.amenities.xl-seat");
    default:
      return "";
  }
}

/**
 * @param translator
 * @param keys
 * @returns
 */
export function getAmenityKeysTranslations(
  translator: TranslatorService,
  keys: (keyof NapiAmenities)[]
): string[] {
  return keys.map(amenity_key => getLabelText(translator, amenity_key));
}

export function getOperatorClassAmenities(
  operator?: NapiOperator | null
): NapiClassAmenities[] {
  if (!operator) {
    return [];
  }
  return Object.values(operator.full_amenities.classes);
}

function normalizeTripAmenities(
  amenities: TripOption["amenities"]
): (keyof NapiAmenities)[] {
  return isAmenitiesArray(amenities)
    ? (amenities as string[])
    : filterSupportedAmenities(amenities);
}

export function shouldHideAmenitiesSelection(
  is_single_class_option: boolean,
  trip_classes: TripOption[][]
): boolean {
  let is_amenities_selection_hidden = false;

  if (is_single_class_option) {
    // @ts-expect-error TS2532 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
    const all_amenities = trip_classes[0]?.[0].amenities;
    if (all_amenities) {
      const normalized_amenities = normalizeTripAmenities(all_amenities);
      is_amenities_selection_hidden = normalized_amenities.length === 0;
    }
  }

  return is_amenities_selection_hidden;
}
